import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import './InfoTooltip.scss';

interface InfoTooltipProps {
  text: string;
  title?: string;
  className?: string;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, title, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const iconRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  const updatePosition = () => {
    if (!iconRef.current) return;

    const rect = iconRef.current.getBoundingClientRect();

    setPosition({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  };

  const handleMouseEnter = () => {
    updatePosition();
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  useLayoutEffect(() => {
    if (!isVisible || !tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 8;

    let left = position.left;

    const halfWidth = tooltipRect.width / 2;

    // Prevent overflow on the left
    if (left - halfWidth < padding) {
      left = halfWidth + padding;
    }

    // Prevent overflow on the right
    if (left + halfWidth > window.innerWidth - padding) {
      left = window.innerWidth - halfWidth - padding;
    }

    if (left !== position.left) {
      setPosition((prev) => ({
        ...prev,
        left,
      }));
    }
  }, [isVisible, position.left]);

  useEffect(() => {
    if (!isVisible) return;

    const handlePositionUpdate = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handlePositionUpdate, true);
    window.addEventListener('resize', handlePositionUpdate);

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isVisible]);

  return (
    <>
      <span
        className={`info-tooltip ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span
          className="info-tooltip-icon"
          ref={iconRef}
        >
          ?
        </span>
      </span>

      {isVisible && createPortal(
        <span
          ref={tooltipRef}
          className="info-tooltip-text-portal"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
        >
          {title && (
            <span className="tooltip-title">{title}</span>
          )}
          <span className="tooltip-text">{text}</span>
        </span>,
        document.body,
      )}
    </>
  );
};

export default InfoTooltip;
